import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, Platform, ActivityIndicator } from 'react-native';
import { getFirestore, collection, query, where, getDocs, doc, updateDoc, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useAdminThemeContext } from '../../contexts/AdminThemeContext';
import VenueCard from './VenueCard';
import { setWebPlaceholderColor, getAdminSearchPlaceholderColor } from '../../utils/setWebPlaceholderColor';
import { attachEmbedCoordinatesToVenuePayload } from '../../utils/mapEmbedUtils';

const NEW_VENUES_COLUMNS_KEY = 'vrc-admin-new-venues-columns';
const NEW_VENUES_SEARCH_ID = 'admin-new-venues-search';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name_asc', label: 'Name A–Z' },
  { value: 'name_desc', label: 'Name Z–A' },
  { value: 'city_asc', label: 'City A–Z' },
];

function getVenueTimestamp(venue) {
  const ts = venue.createdAt || venue.submittedAt;
  if (!ts) return 0;
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (ts.seconds) return ts.seconds * 1000;
  if (ts instanceof Date) return ts.getTime();
  const parsed = new Date(ts).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function NewVenues() {
  const { styles: adminStyles, colors, isDarkMode } = useAdminThemeContext();
  const [newVenues, setNewVenues] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [editingVenue, setEditingVenue] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [columnCount, setColumnCount] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = parseInt(localStorage.getItem(NEW_VENUES_COLUMNS_KEY), 10);
      if ([1, 2, 3].includes(saved)) return saved;
    }
    return 2;
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(NEW_VENUES_COLUMNS_KEY, String(columnCount));
    }
  }, [columnCount]);

  const searchPlaceholderColor = getAdminSearchPlaceholderColor(isDarkMode);

  useEffect(() => {
    setWebPlaceholderColor(NEW_VENUES_SEARCH_ID, searchPlaceholderColor);
  }, [searchPlaceholderColor]);

  useEffect(() => {
    loadNewVenues();
  }, [filterStatus]);

  const pillStyle = (active, danger = false) => ({
    backgroundColor: active
      ? '#0bb5df'
      : danger
        ? (isDarkMode ? 'rgba(231, 76, 60, 0.2)' : 'rgba(231, 76, 60, 0.12)')
        : colors.cardBgSubtle,
    borderWidth: active ? 0 : 1,
    borderColor: danger
      ? (isDarkMode ? 'rgba(231, 76, 60, 0.4)' : 'rgba(231, 76, 60, 0.25)')
      : colors.cardBorder,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
    justifyContent: 'center',
  });

  const pillTextStyle = (active) => ({
    color: active ? '#ffffff' : colors.textPrimary,
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: active ? '600' : '500',
  });

  const columnBtnStyle = (active) => ({
    width: 36,
    height: 36,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: active ? '#0bb5df' : colors.cardBgSubtle,
    borderWidth: active ? 0 : 1,
    borderColor: colors.cardBorder,
  });

  const gridStyle =
    Platform.OS === 'web'
      ? {
          display: 'grid',
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          gap: 16,
          width: '100%',
          alignItems: 'start',
        }
      : {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: 16,
          width: '100%',
        };

  const cardWrapStyle =
    Platform.OS === 'web'
      ? { minWidth: 0, width: '100%' }
      : { width: columnCount === 1 ? '100%' : columnCount === 2 ? '48%' : '31%' };

  const loadNewVenues = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const venuesRef = collection(db, 'new_venues');

      let q;
      if (filterStatus === 'all') {
        q = query(venuesRef);
      } else {
        q = query(venuesRef, where('status', '==', filterStatus));
      }

      const querySnapshot = await getDocs(q);
      const venues = [];

      querySnapshot.forEach((docSnap) => {
        venues.push({
          id: docSnap.id,
          ...docSnap.data(),
        });
      });

      setNewVenues(venues);
    } catch (error) {
      console.error('Error loading new venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (venue) => {
    try {
      const db = getFirestore();
      const auth = getAuth();

      const embedCoords = await attachEmbedCoordinatesToVenuePayload(venue);

      const venueData = {
        name: venue.name,
        address: venue.address || '',
        city: venue.city || '',
        state: venue.state || '',
        capacity: venue.capacity || '',
        website: venue.website || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: venue.submittedBy?.uid || auth.currentUser?.uid,
        status: 'active',
        stage: venue.stage || '',
        power: venue.power || '',
        lightingControl: venue.lightingControl || '',
        lightingFixtures: venue.lightingFixtures || '',
        video: venue.video || '',
        foh: venue.foh || '',
        pa: venue.pa || '',
        monitors: venue.monitors || '',
        affiliate: venue.affiliate || '',
        backline: venue.backline || '',
        backstage: venue.backstage || '',
        merch: venue.merch || '',
        parking: venue.parking || '',
        shorePower: venue.shorePower || '',
        specSheetLink: venue.specSheetLink || '',
        mapEmbedLink: venue.mapEmbedLink || '',
        notes: venue.notes || '',
        ...embedCoords,
      };

      const venueRef = await addDoc(collection(db, 'venues'), venueData);

      const submissionRef = doc(db, 'new_venues', venue.id);
      await updateDoc(submissionRef, {
        status: 'approved',
        venueId: venueRef.id,
        approvedAt: new Date(),
        approvedBy: auth.currentUser?.uid,
      });

      loadNewVenues();
      alert('Venue approved successfully!');
    } catch (error) {
      console.error('Error approving venue:', error);
      alert('Error approving venue: ' + error.message);
    }
  };

  const handleReject = async (venue) => {
    try {
      const db = getFirestore();
      const auth = getAuth();

      const submissionRef = doc(db, 'new_venues', venue.id);
      await updateDoc(submissionRef, {
        status: 'rejected',
        rejectedAt: new Date(),
        rejectedBy: auth.currentUser?.uid,
      });

      loadNewVenues();
      alert('Venue rejected successfully!');
    } catch (error) {
      console.error('Error rejecting venue:', error);
      alert('Error rejecting venue: ' + error.message);
    }
  };

  const handleRevertToPending = async (venue) => {
    try {
      const db = getFirestore();
      const submissionRef = doc(db, 'new_venues', venue.id);
      await updateDoc(submissionRef, {
        status: 'pending',
        approvedAt: null,
        rejectedAt: null,
        approvedBy: null,
        rejectedBy: null,
      });

      loadNewVenues();
      alert('Venue reverted to pending successfully!');
    } catch (error) {
      console.error('Error reverting venue:', error);
      alert('Error reverting venue: ' + error.message);
    }
  };

  const handleEditField = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveVenue = async () => {
    try {
      if (!editingVenue) return;

      const db = getFirestore();
      const venueRef = doc(db, 'new_venues', editingVenue.id);

      await updateDoc(venueRef, {
        ...editFormData,
        updatedAt: new Date(),
      });

      loadNewVenues();
      setEditingVenue(null);
      setEditFormData({});
      alert('Venue updated successfully!');
    } catch (error) {
      console.error('Error updating venue:', error);
      alert('Error updating venue: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingVenue(null);
    setEditFormData({});
  };

  const startEditing = (venue) => {
    setEditingVenue(venue);
    setEditFormData({
      name: venue.name || '',
      address: venue.address || '',
      city: venue.city || '',
      state: venue.state || '',
      capacity: venue.capacity || '',
      website: venue.website || '',
      stage: venue.stage || '',
      power: venue.power || '',
      lightingControl: venue.lightingControl || '',
      lightingFixtures: venue.lightingFixtures || '',
      video: venue.video || '',
      foh: venue.foh || '',
      pa: venue.pa || '',
      monitors: venue.monitors || '',
      affiliate: venue.affiliate || '',
      backline: venue.backline || '',
      backstage: venue.backstage || '',
      merch: venue.merch || '',
      parking: venue.parking || '',
      shorePower: venue.shorePower || '',
      specSheetLink: venue.specSheetLink || '',
      mapEmbedLink: venue.mapEmbedLink || '',
      notes: venue.notes || '',
      manualsLinks: venue.manualsLinks || [],
      specSheets: venue.specSheets || [],
    });
  };

  const sortVenues = (venues) => {
    const sorted = [...venues];
    switch (sortBy) {
      case 'oldest':
        sorted.sort((a, b) => getVenueTimestamp(a) - getVenueTimestamp(b));
        break;
      case 'name_asc':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name_desc':
        sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'city_asc':
        sorted.sort((a, b) => {
          const cityCompare = (a.city || '').localeCompare(b.city || '');
          return cityCompare !== 0 ? cityCompare : (a.name || '').localeCompare(b.name || '');
        });
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => getVenueTimestamp(b) - getVenueTimestamp(a));
        break;
    }
    return sorted;
  };

  const getFilteredVenues = () => {
    const filteredVenues = newVenues.filter((venue) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        venue.name?.toLowerCase().includes(searchLower) ||
        venue.city?.toLowerCase().includes(searchLower) ||
        venue.state?.toLowerCase().includes(searchLower)
      );
    });

    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();

      return filteredVenues.sort((a, b) => {
        const aExactName = a.name?.toLowerCase() === searchLower;
        const bExactName = b.name?.toLowerCase() === searchLower;
        const aExactCity = a.city?.toLowerCase() === searchLower;
        const bExactCity = b.city?.toLowerCase() === searchLower;
        const aExactState = a.state?.toLowerCase() === searchLower;
        const bExactState = b.state?.toLowerCase() === searchLower;

        if (aExactName && !bExactName) return -1;
        if (!aExactName && bExactName) return 1;
        if (aExactCity && !bExactCity) return -1;
        if (!aExactCity && bExactCity) return 1;
        if (aExactState && !bExactState) return -1;
        if (!aExactState && bExactState) return 1;

        const aStartsWithName = a.name?.toLowerCase().startsWith(searchLower);
        const bStartsWithName = b.name?.toLowerCase().startsWith(searchLower);

        if (aStartsWithName && !bStartsWithName) return -1;
        if (!aStartsWithName && bStartsWithName) return 1;

        return getVenueTimestamp(b) - getVenueTimestamp(a);
      });
    }

    return sortVenues(filteredVenues);
  };

  const handleExportCSV = async () => {
    try {
      const escapeField = (field) => {
        if (field === null || field === undefined) return '';
        const stringField = String(field);
        const cleanField = stringField.replace(/^\uFEFF/, '');
        if (cleanField.includes('"') || cleanField.includes(',') || cleanField.includes('\n') || cleanField.includes('\r')) {
          return `"${cleanField.replace(/"/g, '""')}"`;
        }
        return cleanField;
      };

      const db = getFirestore();
      const venuesRef = collection(db, 'new_venues');
      const q = query(venuesRef, where('status', '==', 'approved'));
      const querySnapshot = await getDocs(q);

      const approvedVenues = [];
      querySnapshot.forEach((docSnap) => {
        approvedVenues.push({
          id: docSnap.id,
          ...docSnap.data(),
        });
      });

      if (approvedVenues.length === 0) {
        alert('No approved venues to export.');
        return;
      }

      const headers = [
        'STATE/COUNTRY', 'VENUE', 'CITY', 'WEBSITE', 'CAPACITY', 'AFFILIATE', 'STAGE',
        'SPEC SHEET LINK', 'POWER', 'LIGHTING CONTROL', 'LIGHTING FIXTURES', 'VIDEO',
        'FOH', 'PA', 'MONITORS', 'BACKSTAGE', 'BACKLINE', 'MERCH', 'PARKING',
        'SHORE POWER', 'NOTES', 'MAP EMBED LINK', 'UPDATED AT', 'STATUS', 'SUBMITTED AT', 'SUBMITTED BY',
      ];

      const csvRows = [
        headers.join(','),
        ...approvedVenues.map((venue) => [
          escapeField(venue.state),
          escapeField(venue.name),
          escapeField(venue.city),
          escapeField(venue.website),
          escapeField(venue.capacity),
          escapeField(venue.affiliate),
          escapeField(venue.stage),
          escapeField(venue.specSheetLink),
          escapeField(venue.power),
          escapeField(venue.lightingControl),
          escapeField(venue.lightingFixtures),
          escapeField(venue.video),
          escapeField(venue.foh),
          escapeField(venue.pa),
          escapeField(venue.monitors),
          escapeField(venue.backstage),
          escapeField(venue.backline),
          escapeField(venue.merch),
          escapeField(venue.parking),
          escapeField(venue.shorePower),
          escapeField(venue.notes),
          escapeField(venue.mapEmbedLink),
          escapeField(venue.updatedAt ? new Date(venue.updatedAt.seconds * 1000).toISOString() : ''),
          escapeField(venue.status),
          escapeField(venue.createdAt ? new Date(venue.createdAt.seconds * 1000).toISOString() : ''),
          escapeField(venue.submittedBy?.email || ''),
        ].join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `approved_new_venues_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting venues:', error);
      alert('Error exporting venues: ' + error.message);
    }
  };

  const filteredVenues = getFilteredVenues();

  return (
    <View style={{ width: '100%' }}>
      <View style={{ width: '100%', marginBottom: 20, gap: 14 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 13, fontWeight: '600' }}>
              Columns
            </Text>
            {[1, 2, 3].map((n) => (
              <TouchableOpacity
                key={n}
                style={columnBtnStyle(columnCount === n)}
                onPress={() => setColumnCount(n)}
                accessibilityLabel={`${n} column layout`}
              >
                <Text style={{ color: columnCount === n ? '#fff' : colors.textPrimary, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>
                  {n}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity
            style={{
              backgroundColor: '#3D7FE6',
              paddingVertical: 12,
              paddingHorizontal: 20,
              borderRadius: 8,
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onPress={handleExportCSV}
          >
            <Text style={{ color: '#ffffff', fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600' }}>
              Export to CSV
            </Text>
          </TouchableOpacity>
        </View>

        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <TouchableOpacity
              key={status}
              style={pillStyle(filterStatus === status, status === 'rejected')}
              onPress={() => setFilterStatus(status)}
            >
              <Text style={pillTextStyle(filterStatus === status)}>{status.toUpperCase()}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <View>
          <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600', marginBottom: 10 }}>
            Sort by:
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
            {SORT_OPTIONS.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={pillStyle(sortBy === option.value)}
                onPress={() => setSortBy(option.value)}
              >
                <Text style={pillTextStyle(sortBy === option.value)}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        <TextInput
          nativeID={NEW_VENUES_SEARCH_ID}
          style={{
            backgroundColor: colors.inputBg,
            borderRadius: 8,
            padding: 15,
            color: colors.inputText,
            fontFamily: 'VastagoGrotesk',
            fontSize: 16,
            width: '100%',
            borderWidth: 1,
            borderColor: colors.inputBorder,
          }}
          placeholder="Search venues by name, city, or state..."
          placeholderTextColor={searchPlaceholderColor}
          value={searchQuery}
          onChangeText={setSearchQuery}
        />

        <Text style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk', fontSize: 14 }}>
          {filteredVenues.length} venue{filteredVenues.length === 1 ? '' : 's'} found
        </Text>
      </View>

      <ScrollView style={{ width: '100%' }} contentContainerStyle={{ paddingBottom: 40 }}>
        {loading ? (
          <View style={{ alignItems: 'center', paddingVertical: 40 }}>
            <ActivityIndicator size="large" color={colors.statAccent} />
            <Text style={[adminStyles.loadingText, { marginTop: 12 }]}>Loading venues...</Text>
          </View>
        ) : filteredVenues.length === 0 ? (
          <Text style={adminStyles.emptyStateText}>No venues found</Text>
        ) : (
          <View style={gridStyle}>
            {filteredVenues.map((venue) => (
              <View key={venue.id} style={cardWrapStyle}>
                <VenueCard
                  venue={venue}
                  editingVenue={editingVenue}
                  editFormData={editFormData}
                  onStartEditing={startEditing}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onRevertToPending={handleRevertToPending}
                  onCancelEdit={handleCancelEdit}
                  onSaveVenue={handleSaveVenue}
                  onEditField={handleEditField}
                />
              </View>
            ))}
          </View>
        )}
      </ScrollView>
    </View>
  );
}
